import { useEffect, useState } from "react";
import { RefreshCw, Save, PenLine } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/glossyApi";

export default function EditorsLetter() {
  const [body, setBody] = useState("");
  const [meta, setMeta] = useState({});
  const [busy, setBusy] = useState("");
  const [brief, setBrief] = useState("");

  const load = () => client.get("/admin/editors-letter").then((r) => { setBody(r.data.body); setMeta(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save");
    try {
      await client.put("/admin/editors-letter", { body });
      toast.success("Editor's Letter saved — live on the homepage now.");
      setMeta((m) => ({ ...m, is_default: false, auto: false }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    setBusy("");
  };

  const regen = async () => {
    if (!window.confirm("Ask the desk to write a fresh letter for this edition? Uses a small amount of AI credit and replaces the current text.")) return;
    setBusy("regen");
    try {
      const r = await client.post("/admin/editors-letter/regenerate", { brief });
      setBody(r.data.body);
      toast.success(brief.trim() ? "Fresh letter drafted from your brief — edit it or leave it as is." : "Fresh letter drafted — edit it or leave it as is.");
    } catch (e) { toast.error(e?.response?.data?.detail || "The desk couldn't draft a letter just now."); }
    setBusy("");
  };

  return (
    <div className="py-8 max-w-4xl" data-testid="editors-letter-panel">
      <p className="font-sans text-sm text-ink/60 mb-4">
        The letter at the heart of the homepage. <b>A fresh one is written automatically for every fortnightly edition</b> — you can rewrite or tweak it here any time. Issue: <b>{meta.issue}</b>
        {meta.is_default && <span className="text-gloss-gold"> · currently showing the house default until this edition's letter is drafted</span>}
        {meta.auto && !meta.is_default && <span className="text-ink/45"> · drafted by the desk</span>}
      </p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={7}
        className="w-full border border-ink/25 bg-white p-4 font-serif text-lg leading-relaxed focus:outline-none focus:border-gloss-gold transition-colors"
        data-testid="editors-letter-body"
      />
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={save} disabled={!!busy} className="bg-ink text-cream px-6 py-3 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="editors-letter-save">
          {busy === "save" ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save letter
        </button>
      </div>
      <div className="mt-6 border border-ink/20 bg-white p-4" data-testid="editors-letter-brief-panel">
        <p className="overline text-[10px] text-ink/60 mb-1">Your brief — what should this letter be about?</p>
        <p className="font-sans text-xs text-ink/50 mb-2">Type what you want the letter to say and the desk writes it from YOUR brief — your subject, your angle, your points. Leave it empty and the desk picks its own theme.</p>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={3}
          placeholder="e.g. This edition is about our new Noticeboard — tell readers every offer on it comes from a real local business they can buy from today…"
          className="w-full border border-ink/25 bg-white p-3 font-sans text-sm leading-relaxed focus:outline-none focus:border-gloss-gold transition-colors"
          data-testid="editors-letter-brief"
        />
        <button onClick={regen} disabled={!!busy} className="mt-2 border border-ink/40 px-6 py-3 overline flex items-center gap-2 hover:border-ink transition-colors disabled:opacity-60" data-testid="editors-letter-regen">
          {busy === "regen" ? <RefreshCw size={14} className="animate-spin" /> : <PenLine size={14} />} {brief.trim() ? "Write it from my brief" : "Ask the desk to rewrite"}
        </button>
      </div>
    </div>
  );
}
